import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import { Tooltip } from '@progress/kendo-react-tooltip';
import CopyToClipboard from 'react-copy-to-clipboard';

import FleetAircraftAccordion, { FleetAccordionPanel } from '@shared/components/FleetAircraftAccordion';
import { FleetAircraft } from '@shared/components/FleetAircraftList';
import phoneIcon from './phone-blue.svg';
import emailIcon from './email-blue.svg';
import addressIcon from './marker-blue.svg';
import preferredIcon from './green-tick.svg';
import documentIcon from './file-icon.svg';
import doNotUseBadge from './doNotUse.svg';
import approvedBadge from './tickApproved.svg';
import classes from './OperatorOverview.module.scss';

export interface OperatorPhone {
  countryCode?: string | null;
  number: string;
  type?: string | null;
  preferred?: boolean;
}

export interface OperatorEmail {
  address: string;
  type?: string | null;
  preferred?: boolean;
}

export interface OperatorDocument {
  key: string;
  name: string;
  url: string;
  thumbnailUrl?: string | null;
}

interface OperatorOverviewProps {
  name: string;
  phones?: OperatorPhone[];
  emails?: OperatorEmail[];
  address?: string;
  baseOfOperation?: string;
  argusRating?: string;
  wyvernRating?: string;
  /** Jets rating: false renders the Do Not Use badge. */
  approved: boolean;
  grade?: string;
  notes?: string;
  fleets?: FleetAccordionPanel[];
  documents?: OperatorDocument[];
  /** Avinode's disclaimer belongs on aircraft documents, not operator ones. */
  documentsDisclaimer?: string;
  onAircraftClick?: (aircraft: FleetAircraft) => void;
  onCopyEmail?: (address: string) => void;
}

const OperatorOverview = ({
  name,
  phones = [],
  emails = [],
  address,
  baseOfOperation,
  argusRating,
  wyvernRating,
  approved,
  grade,
  notes,
  fleets = [],
  documents = [],
  documentsDisclaimer,
  onAircraftClick,
  onCopyEmail,
}: OperatorOverviewProps): JSX.Element => {
  const hasReach = phones.length > 0 || emails.length > 0 || Boolean(address);

  return (
    <div className={classes.operatorOverview}>
      <Tooltip anchorElement="target" position="top" openDelay={100}>
        <h3 className="heading" title={name}>{name}</h3>
      </Tooltip>

      {hasReach && <div className="rule" />}

      {hasReach && (
        <Tooltip anchorElement="target" position="bottom" openDelay={100}>
          <div className="reach">
            {phones.length > 0 && (
              <p className="line">
                <img className="glyph" src={phoneIcon} alt="" />
                <span className="values">
                  {phones.map((phone) => (
                    <span className="value" key={`${phone.countryCode ?? ''}${phone.number}`}>
                      <span title={phone.type ?? undefined}>
                        {phone.countryCode ? `${phone.countryCode} ` : ''}{phone.number}
                      </span>
                      {phone.preferred && <img className="preferred" src={preferredIcon} alt="Preferred" />}
                    </span>
                  ))}
                </span>
              </p>
            )}

            {emails.length > 0 && (
              <p className="line">
                <img className="glyph" src={emailIcon} alt="" />
                <span className="values">
                  {emails.map((email) => (
                    <span className="value" key={email.address}>
                      <a href={`mailto:${email.address}`} title={email.type ?? undefined}>{email.address}</a>
                      <CopyToClipboard text={email.address} onCopy={() => onCopyEmail?.(email.address)}>
                        <button type="button" className="copy" title="Copy email" aria-label={`Copy ${email.address}`} />
                      </CopyToClipboard>
                      {email.preferred && <img className="preferred" src={preferredIcon} alt="Preferred" />}
                    </span>
                  ))}
                </span>
              </p>
            )}

            {address && (
              <p className="line">
                <img className="glyph" src={addressIcon} alt="" />
                <span className="values">{address}</span>
              </p>
            )}
          </div>
        </Tooltip>
      )}

      <div className="rule" />

      <div className="stats">
        {baseOfOperation && (
          <div className="stat">
            <span className="caption">Base of Operations</span>
            <span className="readout">{baseOfOperation}</span>
          </div>
        )}
        {argusRating && (
          <div className="stat">
            <span className="caption">Argus Rating</span>
            <span className="readout">{argusRating}</span>
          </div>
        )}
        {wyvernRating && (
          <div className="stat">
            <span className="caption">Wyvern Rating</span>
            <span className="readout">{wyvernRating}</span>
          </div>
        )}
        <div className="stat">
          <span className="caption">Jets Rating</span>
          <span className="readout">
            <img
              className={approved ? 'approval' : 'approval wide'}
              src={approved ? approvedBadge : doNotUseBadge}
              alt={approved ? 'Approved' : 'Do not use'}
            />
          </span>
        </div>
        {grade && (
          <div className="stat">
            <span className="caption">Grade</span>
            <span className="readout"><span className="chip">{grade}</span></span>
          </div>
        )}
      </div>

      {notes && (
        <div className="remark">
          <span className="caption">Operator Notes</span>
          <p className="readout">{notes}</p>
        </div>
      )}

      {fleets.length > 0 && (
        <div className="fleets">
          <div className="rule" />
          <span className="caption">Fleets:</span>
          <FleetAircraftAccordion panels={fleets} onAircraftClick={onAircraftClick} />
        </div>
      )}

      {documents.length > 0 && (
        <div className="docs">
          <div className="rule" />
          <PanelBar>
            <PanelBarItem expanded title="Documents">
              <div className="content">
                {documentsDisclaimer && <p className="disclaimer">{documentsDisclaimer}</p>}
                {documents.map((file) => (
                  <a
                    className="doc"
                    key={file.key}
                    href={file.url}
                    download={file.name}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={file.thumbnailUrl || documentIcon} alt="" />
                    <span>{file.name}</span>
                  </a>
                ))}
              </div>
            </PanelBarItem>
          </PanelBar>
        </div>
      )}
    </div>
  );
};

export default OperatorOverview;
